"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * The catalog's filter chip (handoff v2 §6.2): one shape for every axis of the filter block —
 * what to show, department, category, the attribute triggers and "Destaques". 44px, 11px × 17px,
 * 13px; the active one inverted to oxide (canon §10: "sempre um filtro ativo, invertido para
 * oxide"), its count dimmed porcelain. The DS's `FilterChip` is a smaller, green-tinted chip, so
 * the catalog draws its own.
 */

export type CatalogChipProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  label: React.ReactNode;
  isActive: boolean;
  /** The count after the label; an empty string keeps its 8px gap, as the prototype's does. */
  count?: string;
  /** An attribute trigger draws the 12px chevron, turned half way while its layer is open. */
  chevron?: { isOpen: boolean };
  /**
   * "Destaques" off draws its count at the caption size where every other chip uses 11px — the
   * prototype's own markup, kept so the two rows measure alike.
   */
  countSize?: "chip" | "caption";
};

export const CatalogChip = React.forwardRef<HTMLButtonElement, CatalogChipProps>(
  ({ label, isActive, count, chevron, countSize = "chip", className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex min-h-[var(--tb-touch-min)] flex-none cursor-pointer items-center gap-2 whitespace-nowrap rounded-[var(--tb-radius-pill)] border px-[17px] py-[11px] font-sans text-[13px] leading-[1.3] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]",
        isActive
          ? "border-[var(--fg)] bg-[var(--fg)] text-[var(--tb-porcelain)]"
          : "border-[var(--tb-border)] bg-tb-white text-[var(--fg)] transition-[border-color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:border-[var(--tb-border-strong)]",
        className,
      )}
      {...props}
    >
      {label}
      {count === undefined ? null : (
        <span
          className={cn(
            "font-mono tabular-nums",
            !isActive && countSize === "caption" ? "text-[12px]" : "text-[11px]",
            isActive ? "text-[rgb(245_237_230_/_0.72)]" : "text-[var(--fg-subtle)]",
          )}
        >
          {count}
        </span>
      )}
      {chevron === undefined ? null : (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={cn(
            "flex-none transition-transform duration-[280ms] ease-[var(--tb-ease-linen)]",
            chevron.isOpen ? "rotate-180" : "rotate-0",
          )}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      )}
    </button>
  ),
);
CatalogChip.displayName = "CatalogChip";
